import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import PhotoGallery from "../components/PhotoGallery";
import DeleteAccountSection from "../components/DeleteAccountSection";
import { useAuth, authFetch } from "../context/AuthContext";
import { toast } from "sonner";

const SIGN_GLYPH = {
    Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
    Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};

// Convert "HH:MM" 24h string to "h:MM AM/PM" for display confirmation
function fmt12(hhmm) {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
    const period = h < 12 ? "AM" : "PM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

export default function Profile() {
    const { user, refresh, setUser } = useAuth();
    const [bio, setBio] = useState(user?.bio || "");
    const [interestedIn, setInterestedIn] = useState(user?.interested_in || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await authFetch("patch", "/profile", { bio, interested_in: interestedIn });
            await refresh();
            toast.success("Profile updated");
        } catch {
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    if (!user) return null;

    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-28 pb-12 px-6">
                <div className="max-w-2xl mx-auto space-y-6">
                    <div className="text-center">
                        <p className="label-tracking text-gold">Your Cosmic Profile</p>
                        <h1 className="font-serif text-5xl mt-2">{user.name}</h1>
                        <p className="text-muted mt-2">{user.email}</p>
                    </div>

                    {/* Big Three Display */}
                    <div className="glass-gold p-6">
                        <p className="label-tracking text-gold text-center">Your Signature</p>
                        <div className="grid grid-cols-3 gap-4 mt-4">
                            {[
                                { label: "Sun", sign: user.sun },
                                { label: "Moon", sign: user.moon },
                                { label: "Rising", sign: user.rising },
                            ].map((s) => (
                                <div key={s.label} className="text-center">
                                    <p className="label-tracking text-xs">{s.label}</p>
                                    <p className="font-serif text-4xl text-gold mt-2">{SIGN_GLYPH[s.sign]}</p>
                                    <p className="font-serif">{s.sign}</p>
                                </div>
                            ))}
                        </div>
                        <p className="text-center text-xs text-muted mt-4">
                            {user.birth_date} · {user.birth_time && fmt12(user.birth_time)} · {user.birth_location}
                        </p>
                        <div className="text-center mt-4">
                            <Link
                                to="/onboarding"
                                className="text-xs label-tracking text-gold hover:opacity-80 transition-opacity"
                                data-testid="edit-birth-info"
                            >
                                ✦ Edit Birth Info & Recast Chart
                            </Link>
                        </div>
                    </div>

                    {/* Photos & Verification */}
                    <PhotoGallery
                        photos={user.photos || []}
                        verified={user.verified}
                        onChange={(photos) => setUser((u) => ({ ...u, photos }))}
                        onVerify={(verified) => { setUser((u) => ({ ...u, verified })); refresh(); }}
                    />

                    {/* Edit Form */}
                    <div className="glass p-6">
                        <p className="label-tracking text-gold mb-4">Edit Profile</p>
                        <div className="space-y-4">
                            <div>
                                <label className="label-tracking">Bio</label>
                                <textarea
                                    rows={4}
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    className="w-full mt-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-gold outline-none"
                                    data-testid="profile-bio"
                                />
                            </div>
                            <div>
                                <label className="label-tracking">Interested in</label>
                                <select
                                    value={interestedIn}
                                    onChange={(e) => setInterestedIn(e.target.value)}
                                    className="w-full mt-2 px-4 py-3 rounded-xl bg-[#0A0A1A] border border-white/10 focus:border-gold outline-none"
                                    data-testid="profile-interested"
                                >
                                    <option value="woman">Women</option>
                                    <option value="man">Men</option>
                                    <option value="all">Everyone</option>
                                </select>
                            </div>
                            <button onClick={save} disabled={saving} className="btn-gold-solid w-full" data-testid="profile-save">
                                {saving ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </div>

                    {/* Subscription Status */}
                    <div className="glass p-6 text-center">
                        <p className="label-tracking text-gold">Subscription</p>
                        <p className="font-serif text-2xl mt-2">
                            {user.subscription_active ? "✦ Cosmic Premium" : "Free Tier"}
                        </p>
                        {user.subscription_active && (
                            <p className="text-sm text-muted mt-1">Expires {new Date(user.subscription_expires).toLocaleDateString()}</p>
                        )}
                    </div>

                    {/* Delete Account (Danger Zone) */}
                    <DeleteAccountSection />
                </div>
            </main>
        </>
    );
}
